import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Package, History, Layers, BookUser, Settings, LogOut } from 'lucide-react';
import { Badge, Button, Dialog, Heading, Text } from '@elladx/design-system';
import { useSessionAction } from './SessionActionContext';

const NAV = [
  { path: '/', label: 'Create shipment', icon: Package },
  { path: '/orders', label: 'Orders', icon: History },
  { path: '/bulk-ship', label: 'Bulk ship', icon: Layers },
  { path: '/addresses', label: 'Recipients', icon: BookUser },
  { path: '/settings', label: 'Settings', icon: Settings },
];

function Layout() {
  const session = useSessionAction();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  const crumb = NAV.find(item => item.path === location.pathname)?.label ?? 'Workspace';
  const initials = (session?.userName || session?.userEmail || 'Operator').split(/\s+|@/).slice(0, 2).map(part => part[0] || '').join('').toUpperCase();

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const update = () => { setIsMobile(media.matches); setMenuOpen(false); };
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    let baselineHeight = Math.max(window.innerHeight, viewport.height);
    let baselineWidth = window.innerWidth;
    const update = () => {
      const active = document.activeElement;
      const editing = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || (active instanceof HTMLElement && active.isContentEditable);
      if (Math.abs(window.innerWidth - baselineWidth) > 80) {
        baselineWidth = window.innerWidth;
        baselineHeight = Math.max(window.innerHeight, viewport.height);
      } else if (!editing) {
        baselineHeight = Math.max(baselineHeight, window.innerHeight, viewport.height);
      }
      setKeyboardOpen(editing && Math.max(baselineHeight, window.innerHeight) - viewport.height > 150);
    };
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    window.addEventListener('focusin', update);
    window.addEventListener('focusout', update);
    window.addEventListener('resize', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      window.removeEventListener('focusin', update);
      window.removeEventListener('focusout', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const endSession = () => {
    if (!session || session.disabled) return;
    const event = new CustomEvent('easypost:before-session-end', { cancelable: true, detail: { proceed: session.onAction } });
    if (window.dispatchEvent(event)) session.onAction();
  };
  const navigation = (id: string) => (
    <nav id={id} className="sidebar-nav" aria-label="Main navigation">
      {NAV.map(({ path, label, icon: Icon }, index) => (
        <div key={path}>
          {(index === 0 || index === 3) && <Text size="small" tone="muted" className="nav-label">{index === 0 ? 'Shipping' : 'Manage'}</Text>}
          <Button asChild variant="ghost" className="workspace-nav-link">
            <NavLink to={path} end={path === '/'} title={label} onClick={() => setMenuOpen(false)}>
              <Icon size={18} strokeWidth={1.75} aria-hidden="true" /><span>{label}</span>
            </NavLink>
          </Button>
        </div>
      ))}
    </nav>
  );
  const sessionControls = session && (
    <div className="sidebar-foot">
      <Text className="operator-name">{session.userName || 'Operator'}</Text>
      {session.userEmail && <Text size="small" tone="muted" className="operator-email">{session.userEmail}</Text>}
      <Button variant="ghost" onClick={endSession} disabled={session.disabled}
        title={session.actionTitle || session.actionLabel} className="session-action">
        <LogOut size={18} aria-hidden="true" /><span>{session.actionLabel}</span>
      </Button>
    </div>
  );

  return (
    <div className={`app${keyboardOpen ? ' app--keyboard-open' : ''}`}>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <header className="mobile-header">
        <Heading as="span" variant="card">EasyPost</Heading>
        <Button variant="secondary" className="workspace-account" aria-label="Open navigation" aria-haspopup="dialog" aria-expanded={menuOpen}
          aria-controls="workspace-navigation" onClick={() => setMenuOpen(true)}>{initials}</Button>
      </header>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <Heading as="div" variant="display">EasyPost</Heading>
          <Text size="small" tone="muted">Fifty410 · Shipping console</Text>
        </div>
        {navigation('desktop-workspace-navigation')}
        {sessionControls}
      </aside>
      <Dialog open={menuOpen} onClose={() => setMenuOpen(false)} title={isMobile ? 'EasyPost' : 'Your workspace'}
        variant={isMobile ? 'drawer' : undefined} closeLabel="Close navigation" className="workspace-drawer">
        <Text size="small" tone="muted">Fifty410 · Shipping console</Text>
        {isMobile ? navigation('workspace-navigation') : <nav className="sidebar-nav" aria-label="Account navigation">
          {NAV.filter(item => item.path === '/bulk-ship' || item.path === '/settings').map(({ path, label, icon: Icon }) => <Button key={path} asChild variant="ghost" className="workspace-nav-link">
            <NavLink to={path} onClick={() => setMenuOpen(false)}><Icon size={18} aria-hidden="true" /><span>{label}</span></NavLink>
          </Button>)}
        </nav>}
        {sessionControls}
      </Dialog>
      {isMobile && !keyboardOpen && <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
        {[
          { path: '/', label: 'Create', icon: Package },
          { path: '/orders', label: 'Orders', icon: History },
          { path: '/addresses', label: 'Recipients', icon: BookUser },
        ].map(({ path, label, icon: Icon }) => <Button key={path} asChild variant="ghost" className="mobile-bottom-link">
          <NavLink to={path} end={path === '/'}><Icon size={20} aria-hidden="true" /><span>{label}</span></NavLink>
        </Button>)}
      </nav>}
      <div className="app-col">
        <header className="topbar">
          <Text size="small" tone="muted">Fifty410 / {crumb}</Text><div className="topbar-account"><Badge>Production</Badge>
            <Button variant="secondary" className="workspace-account" aria-label="Open account menu" aria-haspopup="dialog" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}>{initials}</Button>
          </div>
        </header>
        <main id="main-content" className="app-main" tabIndex={-1}><div className="page"><Outlet /></div></main>
      </div>
    </div>
  );
}
export default Layout;
